import type { Animation } from "@blings/blings-player";
import { motionAPI } from "@blings/blings-player/editor-tools";
import type { FontInput } from "../API";

// Same vocabulary the Lottie renderer (getFontProperties) uses to turn an
// After Effects style name such as "Bold Italic" into CSS weight/style.
const LOTTIE_STYLE_WEIGHTS: Record<string, string> = {
  bold: "700",
  black: "900",
  medium: "500",
  regular: "400",
  normal: "400",
  light: "200",
  thin: "200",
};

function cssVariantFromLottieFont(font?: {
  fStyle?: string;
  fWeight?: string;
}) {
  const words = (font?.fStyle || "").toLowerCase().split(" ");
  const style = words.includes("italic") ? "italic" : "normal";
  let weightFromStyle: string | undefined;
  words.forEach((word) => {
    weightFromStyle = LOTTIE_STYLE_WEIGHTS[word] || weightFromStyle;
  });
  return { style, weight: String(font?.fWeight || weightFromStyle || "400") };
}

const variantKey = ({ family, style, weight }: FontInput) =>
  `${family}#${style}#${weight}`;

export function getVideoPartFonts(
  animation: Animation,
  savedFonts: FontInput[] = []
): FontInput[] {
  const fontsByVariant = new Map<string, FontInput>();
  const keep = ({ family, style, weight, url }: FontInput) =>
    fontsByVariant.set(variantKey({ family, style, weight, url }), {
      family,
      style,
      weight,
      url,
    });
  const familiesWithoutUrl = new Set<string>();

  // Reuse the editor's inspection of text keyframes, including asset layers.
  motionAPI.inspect.getFonts(animation).fonts.forEach((info) => {
    if (!info.layerReferences.length) return;

    const { font, metadata } = info;
    const family = metadata?.family || font?.fFamily;
    if (!family) return;
    // fffDef already speaks CSS; legacy fonts.list entries speak After Effects
    // and must be mapped so they match the variants the platform saved.
    const { style, weight } = metadata
      ? {
          style: String(metadata.style || font?.fStyle || "normal"),
          weight: String(metadata.weight || font?.fWeight || "400"),
        }
      : cssVariantFromLottieFont(font);
    const savedFont = savedFonts.find(
      (candidate) =>
        candidate.family === family &&
        candidate.style === style &&
        candidate.weight === weight
    );
    // Legacy fonts keep the file the platform saved; fPath is often a Google
    // stylesheet that would replace it with a weaker source.
    const url = metadata
      ? metadata.url || font?.fPath || savedFont?.url
      : savedFont?.url || font?.fPath;
    if (url) {
      keep({ family, style, weight, url });
      return;
    }
    familiesWithoutUrl.add(family);
  });

  // ponytail: no URL anywhere for a variant — keep what the platform saved for
  // the family instead of dropping a font the scene still renders with, but
  // never overwrite a variant resolved above (e.g. a font just replaced).
  savedFonts
    .filter((candidate) => familiesWithoutUrl.has(candidate.family))
    .forEach((candidate) => {
      if (!fontsByVariant.has(variantKey(candidate))) keep(candidate);
    });

  return Array.from(fontsByVariant.values());
}
